import React, { useEffect } from 'react';
import { Form, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getVendors } from '@/api/vendors';
import { useAuth } from '@/contexts/AuthContext';
import type { Vendor } from '@/types/resources/vendor';

interface VendorSelectorProps {
  disabled?: boolean;
  fieldName?: string;
  onVendorDataChange?: (vendor: { id: string; name: string } | null) => void;
}

export const VendorSelector: React.FC<VendorSelectorProps> = ({
  disabled = false,
  fieldName = 'vendorId',
  onVendorDataChange
}) => {
  const { t } = useTranslation(['vendors', 'common']);
  const { firestoreUser } = useAuth();
  const form = Form.useFormInstance();

  // Fetch vendors
  const { data: vendorsData, error } = useQuery({
    queryKey: ['vendors', firestoreUser?.workspace?.id],
    queryFn: () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getVendors({ 
        firestoreUser,
        pageSize: 100 // Get all vendors for selector
      });
    },
    enabled: !!firestoreUser?.workspace?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Initialize vendor data from form values when component mounts or form values change
  useEffect(() => {
    const vendorId = form.getFieldValue(fieldName);
    if (vendorId && vendorsData?.vendors) {
      const vendor = vendorsData.vendors.find((v: Vendor) => v.id === vendorId);
      onVendorDataChange?.(vendor ? { 
        id: vendor.id, 
        name: vendor.name 
      } : null);
    }
  }, [form, vendorsData, onVendorDataChange, fieldName]);

  const handleVendorChange = (vendorId: string) => {
    // Provide vendor data to parent
    if (vendorId) {
      const vendor = vendorsData?.vendors?.find((v: Vendor) => v.id === vendorId);
      onVendorDataChange?.(vendor ? { 
        id: vendor.id, 
        name: vendor.name 
      } : null);
    } else {
      onVendorDataChange?.(null);
    }
  };

  const vendorOptions = vendorsData?.vendors?.map((vendor: Vendor) => ({
    value: vendor.id,
    label: vendor.name,
  })) || [];

  // Show error state if API fails
  if (error) {
    return (
      <Form.Item
        name={fieldName}
        label={t('vendors:fields.name')}
      >
        <Select
          placeholder={t('vendors:fields.selectVendor')}
          disabled={true}
          options={[]}
        />
      </Form.Item>
    );
  }

  return (
    <Form.Item
      name={fieldName}
      label={t('vendors:fields.name')}
    >
      <Select
        placeholder={t('vendors:fields.selectVendor')}
        onChange={handleVendorChange}
        disabled={disabled}
        allowClear
        options={vendorOptions}
        showSearch
        filterOption={(input, option) =>
          (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
        }
      />
    </Form.Item>
  );
}; 